import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface InvoiceChargeSummary {
  type: string;
  count: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  totalAmount: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface AgentInvoiceDto {
  invoiceId: string;
  agentId: string;
  agentName: string | null;
  invoiceDate: string;
  totalAmount: number;
  roundOff?: number;
  paidAmount: number;
  balanceDue: number;
  status: string;
  invoiceDownloadUrl: string | null; // SAS URL for direct PDF download
  transactionCount: number;
  createdAt: string;
  updatedAt: string;
  transactions?: AgentTransaction[];
  chargeSummary?: InvoiceChargeSummary[];
}

export interface AgentTransaction {
  agentId: string;
  agentName: string;
  type: string;
  amount: number;
  cgst: number;
  sgst: number;
  totalAmount: number;
  isInvoiced: boolean;
  referenceId: string;
  sourceType: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  id: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgentInvoiceService {
  private apiUrl = `${environment.apiConfig.uri}/api/AgentInvoice`;
  private generateApiUrl = `${environment.apiConfig.uri}/api/Invoice/agents`;

  constructor(private http: HttpClient) { }

  /**
   * Get paginated list of agent invoices with optional filter by agent name
   */
  getAgentInvoices(agentName?: string, pageIndex: number = 0, pageSize: number = 10): Observable<PaginatedResponse<AgentInvoiceDto>> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (agentName) {
      params = params.set('agentName', agentName);
    }
    return this.http.get<PaginatedResponse<AgentInvoiceDto>>(this.apiUrl, { params }).pipe(
      map(response => ({
        ...response,
        items: (response.items || []).map(inv => ({
          ...inv,
          transactions: (inv.transactions || []).map(tx => ({
            ...tx,
            createdAt: tx.createdAt || (tx as any).CreatedAt || '',
            updatedAt: tx.updatedAt || (tx as any).UpdatedAt || '',
            createdBy: tx.createdBy || (tx as any).CreatedBy || '',
            updatedBy: tx.updatedBy || (tx as any).UpdatedBy || ''
          }))
        }))
      }))
    );
  }

  /**
   * Generate a new invoice for a specific agent
   */
  generateAgentInvoice(agentId: string, toDate?: string): Observable<AgentInvoiceDto> {
    let params = new HttpParams();
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    return this.http.post(`${this.generateApiUrl}/${agentId}/generate`, {}, { params, responseType: 'text' }).pipe(
      map(response => {
        const inv = JSON.parse(response) as AgentInvoiceDto;
        return {
          ...inv,
          transactions: (inv.transactions || []).map(tx => ({
            ...tx,
            createdAt: tx.createdAt || (tx as any).CreatedAt || '',
            updatedAt: tx.updatedAt || (tx as any).UpdatedAt || '',
            createdBy: tx.createdBy || (tx as any).CreatedBy || '',
            updatedBy: tx.updatedBy || (tx as any).UpdatedBy || ''
          }))
        };
      })
    );
  }

  /**
   * Get pending and partially paid invoices for a specific agent
   */
  getPendingInvoices(agentId: string): Observable<AgentInvoiceDto[]> {
    return this.http.get<AgentInvoiceDto[]>(`${this.apiUrl}/agent/${agentId}/pending`);
  }

  /**
   * Get uninvoiced transactions for a specific agent
   */
  getUninvoicedTransactions(agentId: string, toDate?: string): Observable<AgentTransaction[]> {
    let params = new HttpParams();
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    return this.http.get<any[]>(`${this.apiUrl}/agent/${agentId}/transactions/uninvoiced`, { params }).pipe(
      map(transactions => transactions.map(tx => ({
        ...tx,
        createdAt: tx.createdAt || tx.CreatedAt,
        updatedAt: tx.updatedAt || tx.UpdatedAt,
        createdBy: tx.createdBy || tx.CreatedBy,
        updatedBy: tx.updatedBy || tx.UpdatedBy
      } as AgentTransaction)))
    );
  }
}

